import {Component, Input, NgZone, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {SelectorOptions} from '../../_types/shared';
import {FormBuilder, FormGroup} from '@angular/forms';
import {FeedPayload, FeedPost} from '../../_types/content';
import {Category, Community} from '../../_types/communities';
import {hasDifferentField} from '../../_utils/objects-is-different';
import {endOfDay, startOfDay} from '../../_utils/date';
import {removeNullUndefinedFields} from '../../_utils/remove-null-fields';
import {EMPTY, Observable, retry, Subject, switchMap, take, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ContentService} from '../../_services/content.service';
import {CommunitiesService} from '../../_services/communities.service';
import {LocalStorageService} from '../../_services/local-storage.service';
import {ActivatedRoute, Router} from '@angular/router';
import {SnackbarService} from "../../_services/snackbar.service";
import {takeUntil} from "rxjs/operators";

@Component({
    selector: 'app-dashboard-feed',
    templateUrl: './dashboard-feed.component.html',
    styleUrls: ['./dashboard-feed.component.scss', '../feed.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DashboardFeedComponent implements OnInit, OnDestroy {

    @Input() isMobile: boolean = false;
    @Input() sidebarPosts: FeedPost[] = [];
    @Input() currentCommunityId: number | null = null;
    sortOptions: SelectorOptions[] = [
        {label: 'Newest first', value: 'newest'},
        {label: 'Oldest first', value: 'oldest'},
        {label: 'Most liked', value: 'most_liked'},
        {label: 'Most Disliked', value: 'most_disliked'},
        {label: 'Most commented', value: 'most_commented', disable: true},
        {label: 'Least commented', value: 'least_commented', disable: true},
    ];
    showPinnedOptions: SelectorOptions[] = [
        {label: 'Show all', value: null},
        {label: 'Only pinned', value: true},
        {label: 'Only unpinned', value: false},
    ];
    filterOptions: SelectorOptions[] = [
        {label: 'Show all', value: ''},
        {label: 'Polls', value: 'Polls'},
        {label: 'Videos', value: 'Videos'},
        {label: 'Discussions', value: 'Discussions'},
        {label: 'Heatmap', value: 'Heatmap'},
        {label: 'Survey', value: 'Survey'},
    ];

    filterGroup: FormGroup = this.fb.group({
        search: this.fb.control(''),
        category: this.fb.control(null),
        isClosedLoop: this.fb.control(''),
        startDate: this.fb.control(null),
        endDate: this.fb.control(null),
        sortBy: this.fb.control(''),
        filterBy: this.fb.control(''),
        isPinned: this.fb.control(null),
    });

    filters: FeedPayload = {};
    categories: Category[] = [];
    content: FeedPost[] = [];

    categoryOptions!: { label: string, value: number | null, color?: string }[];

    subgroup: number | null = null;
    page: number = 1;
    isNextPageAllowed: boolean = false;
    loading: boolean | null = null;
    community: Community | null = null
    setCategory: number | null = null;
    protected readonly location: Location = location;
    private lastFilters: ReturnType<DashboardFeedComponent['filterFormValue'] & { subgroup: number | null }>;
    private filtersSubject: Subject<any> = new Subject<any>();
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly fb: FormBuilder,
        private readonly contentService: ContentService,
        private readonly communitiesService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly snackbarService: SnackbarService,
        private ngZone: NgZone
    ) {
        this.contentService.getCurrentDashboardCommunity()
            .pipe(
                switchMap((community): Observable<Category[]> => {
                    this.ngZone.run((): void => {
                        this.community = community;
                    });
                    return community?.id
                        ? this.getCategories(community.id)
                        : EMPTY;
                }),
                takeUntilDestroyed()
            )
            .subscribe((): void => {
                this.reloadContent();
            });
        this.lastFilters = {
            ...this.filterFormValue,
            subgroup: null
        };
        this.route.queryParams
            .pipe(
                takeUntilDestroyed()
            )
            .subscribe(params => {
                this.subgroup = params['subgroup'] ? Number(params['subgroup']) : null;
                if (params['category']) {
                    this.setCategory = Number(params['category'])
                }
                if (params['closeTheLoop']) {
                    this.setCategory = -1
                }
                this.applyFilters(this.setCategory);
                if (this.setCategory) {
                    // Remove category param so it will work if category filtered later
                    this.router.navigate(['/feed'], {
                        queryParams: {
                            category: null,
                            closeTheLoop: null
                        },
                        queryParamsHandling: 'merge',
                        replaceUrl: false,
                    });
                }
                if (this.subgroup && this.community && this.community?.id) {
                    this.communitiesService.getSubGroups(this.community.id)
                        .subscribe(res => {
                            for (let subgroup of res) {
                                if (subgroup.id == this.subgroup) {


                                    if (subgroup.item_body_text_font_type?.name) {
                                        document.documentElement.style.setProperty(
                                            `--font-family`, "'" + subgroup?.item_body_text_font_type?.name + "'"
                                        );
                                    } else if (this.community?.body_font_type?.name) {
                                        document.documentElement.style.setProperty(
                                            `--font-family`, "'" + this.community?.body_font_type?.name + "'"
                                        );
                                    } else {
                                        document.documentElement.style.setProperty(
                                            `--font-family`, 'roboto'
                                        );
                                    }

                                    if (subgroup.item_header_text_font?.name) {
                                        document.documentElement.style.setProperty(
                                            `--heading-font-family`, "'" + subgroup?.item_header_text_font?.name + "'"
                                        );
                                    } else if (this.community?.heading_font_type?.name) {
                                        document.documentElement.style.setProperty(
                                            `--heading-font-family`, "'" + this.community?.heading_font_type?.name + "'"
                                        );
                                    } else {
                                        document.documentElement.style.setProperty(
                                            `--heading-font-family`, 'roboto'
                                        );
                                    }

                                }
                            }
                        });
                }
            });

    }

    get filterFormValue(): any {
        return this.filterGroup.value;
    }

    get filtersApplied(): boolean {
        return !!Object.values(this.filters).length;
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    get dateSet(): Community | null {
        return this.filterFormValue.startDate || this.filterFormValue.endDate;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.filtersSubject.pipe(
            switchMap(filters => {
                setTimeout((): void => {
                    // Delay loading wheel for 3 seconds.
                    if (this.loading != false) {
                        this.loading = true;
                    }
                }, 2000);
                return this.contentService.getFeed(
                    this.lastSelectedCommunity?.id!,
                    this.page,
                    (this.isMobile ? 5 : 8),
                    filters
                );
            }),
            retry(1),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (result): void => {
                this.content = this.page === 1 ? result.results : this.content.concat(result.results);
                this.isNextPageAllowed = !!result.next;
                this.loading = false;
            },
            error: (e): void => {
                this.snackbarService.openErrorSnackBar(e);
                this.loading = false;
            },
        });
    }

    isRequiredScrollToPost(id: number): boolean {
        return this.route.snapshot.queryParamMap.get('post') === id.toString();
    }

    trackByPostId(index: number, post: any): number {
        return post.id;
    }

    clearSearch(): void {
        this.filterGroup.get('search')?.reset(null);
    }

    setFilters(event: any, filter: string): void {
        if (this.filterGroup.get(filter)?.value !== event) {
            this.filterGroup.get(filter)?.patchValue(event)
        } else if (filter == 'sortBy') {
            this.filterGroup.get(filter)?.patchValue(null)
        }
        this.applyFilters()
    }

    applyFilters(categoryCarouselEvent?: any): void {
        if (categoryCarouselEvent !== undefined) {
            if (categoryCarouselEvent == -1) {
                // close the loop selected
                this.filterGroup.patchValue({category: null, isClosedLoop: true})
            } else if (categoryCarouselEvent == null) {
                // 'All Categories' selected
                this.filterGroup.patchValue({category: null, isClosedLoop: null})
            } else {
                // Category Selected
                this.filterGroup.patchValue({category: categoryCarouselEvent, isClosedLoop: null})
            }
        }
        const filters = {...this.filterFormValue, subgroup: this.subgroup};


        const payload = {
            search: this.filterFormValue.search,
            category: this.filterFormValue.category,
            subgroup: this.subgroup,
            is_closed_loop: this.filterFormValue.isClosedLoop,
            created_range_after: this.filterFormValue.startDate ? startOfDay(this.filterFormValue.startDate)?.toISOString() : null,
            created_range_before: this.filterFormValue.endDate ? endOfDay(this.filterFormValue.endDate)?.toISOString() : null,
            sort_by: this.filterFormValue.sortBy,
            is_pinned: this.filterFormValue.isPinned,
            has_poll: this.filterGroup.value['filterBy'] == 'Polls' ? true : null,
            has_video: this.filterGroup.value['filterBy'] == 'Videos' ? true : null,
            is_discussion: this.filterGroup.value['filterBy'] == 'Discussions' ? true : null,
            has_heatmap: this.filterGroup.value['filterBy'] == 'Heatmap' ? true : null,
            has_survey: this.filterGroup.value['filterBy'] == 'Survey' ? true : null,
        }
        if (!hasDifferentField(this.lastFilters, filters)) return;
        this.lastFilters = filters;
        this.filters = removeNullUndefinedFields(payload);
        this.reloadContent();
    }

    triggerInfiniteScroll() {
        if (!this.isNextPageAllowed || this.loading) return;
        this.page += 1;
        this.loadPosts(this.page);

    }

    reloadContent(): void {
        this.content = [];
        this.page = 1;
        this.loadPosts(1);
    }

    clearDate() {
        this.filterGroup.get('startDate')?.reset(null);
        this.filterGroup.get('endDate')?.reset(null);
        this.applyFilters()
    }

    loadPostFromSidebar(id: number | null): void {
        if (!id) return;
        this.loading = true;
        this.contentService.getFeed(id, 1, 1000, {is_pinned_to_sidebar: true, sort_by: 'newest'})
            .pipe(take(1))
            .subscribe({
                next: result => {
                    this.sidebarPosts = result.results;
                    this.loading = false;
                },
                error: () => this.loading = false
            });
    }

    private loadPosts(page: number): void {
        if (this.lastSelectedCommunity) {
            this.page = page;
            this.filtersSubject.next(this.filters);
        } else {
            this.snackbarService.openErrorSnackBar('No Community Selected');
        }
    }

    private getCategories(id: number): Observable<Category[]> {
        this.categories = [];
        this.categoryOptions = [];
        return this.communitiesService.getCategories(id)
            .pipe(
                tap(res => {
                    this.categories = res;
                    this.categoryOptions = this.categories.map(category => ({
                        value: category.id,
                        label: this.community?.member_generated_name && category.name == 'Member generated' ? this.community?.member_generated_name : category.name,
                        color: category.font_color_hex
                    }));

                    this.categoryOptions.unshift({
                        value: -1,
                        label: this.community?.close_the_loop_name || 'Close the loop'
                    });
                    this.categoryOptions.unshift({
                        value: null,
                        label: this.community?.categories_tile_name || 'All categories'
                    });

                    setTimeout((): void => {
                        for (let i: number = 0; i < this.categoryOptions?.length; i++) {
                            if (this.categoryOptions[i].color) {
                                const tab: HTMLElement | null = document.getElementById("mat-tab-label-0-" + i);
                                const label: HTMLElement | null | undefined = tab?.querySelector<HTMLElement>('.mdc-tab__text-label');
                                if (label) {
                                    // @ts-ignore
                                    label.style.color = this.categoryOptions[i].color;
                                }

                                const activeIndicator: HTMLElement | null | undefined = tab?.querySelector<HTMLElement>('.mdc-tab-indicator__content--underline');
                                if (activeIndicator) {
                                    // @ts-ignore
                                    activeIndicator.style.borderColor = this.categoryOptions[i].color;
                                }
                            }
                        }
                    }, 500);
                })
            );
    }
}
